import { useState } from "react";
import { toast } from "sonner";

import {
  type CarrierDiscountRuleStatus,
  type CarrierDiscountFunding,
  type CarrierLabelDiscountRule,
  useCreateCarrierLabelDiscountRule,
  useUpdateCarrierLabelDiscountRule,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { mutationFailure, useSubmissionGuard } from "./mutation-guards";
import { cn } from "@/lib/utils";

export function LabelDiscountEditor({
  tierName,
  tierId,
  existing,
}: {
  tierName: string;
  tierId: string | null;
  existing: CarrierLabelDiscountRule | null;
}) {
  return (
    <DiscountRuleEditor
      triggerAriaLabel="Edit label discount"
      title="Label discount"
      description="Configure the requested label discount and choose who pays for it. The quote determines the amount that can actually apply."
      ruleName={`${tierName} label discount`}
      existing={existing}
      eligibilityRows={tierId ? [{ carrierCustomerTierId: tierId }] : [{}]}
      successMessage="Label discount saved."
      saveLabel="Save benefit"
    />
  );
}

export function CustomerOverrideButton({
  customerId,
  customerLabel,
  existing,
  fullWidth = false,
}: {
  customerId: string;
  customerLabel: string;
  existing: CarrierLabelDiscountRule | null;
  fullWidth?: boolean;
}) {
  return (
    <DiscountRuleEditor
      triggerAriaLabel={`Override for ${customerLabel}`}
      title={`Override for ${customerLabel}`}
      description="Replaces this customer's tier label discount. The quote determines the amount that can actually apply."
      ruleName={`${customerLabel} label override`}
      existing={existing}
      eligibilityRows={[{ customerId }]}
      successMessage="Customer override saved."
      saveLabel="Save override"
      fullWidth={fullWidth}
    />
  );
}

function DiscountRuleEditor({
  triggerAriaLabel,
  title,
  description,
  ruleName,
  existing,
  eligibilityRows,
  successMessage,
  saveLabel,
  fullWidth = false,
}: {
  triggerAriaLabel: string;
  title: string;
  description: string;
  ruleName: string;
  existing: CarrierLabelDiscountRule | null;
  eligibilityRows: Array<{
    customerId?: string | null;
    carrierCustomerTierId?: string | null;
    carrierServiceId?: string | null;
    dispatchCenterId?: string | null;
  }>;
  successMessage: string;
  saveLabel: string;
  fullWidth?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [pct, setPct] = useState("");
  const [funding, setFunding] = useState<CarrierDiscountFunding | "">("");
  const [status, setStatus] = useState<CarrierDiscountRuleStatus>("ACTIVE");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const createRule = useCreateCarrierLabelDiscountRule();
  const updateRule = useUpdateCarrierLabelDiscountRule();
  const submission = useSubmissionGuard();
  const pending =
    submission.pending || createRule.isPending || updateRule.isPending;
  const fieldId = triggerAriaLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  function start() {
    setPct(existing ? String(existing.percentage) : "");
    setFunding(existing?.funding ?? "");
    setStatus(existing?.status ?? "ACTIVE");
    setStartsAt(existing?.startsAt?.slice(0, 10) ?? "");
    setEndsAt(existing?.endsAt?.slice(0, 10) ?? "");
    setValidationError(null);
    setServerError(null);
    setOpen(true);
  }

  async function submit() {
    if (!funding) {
      setValidationError("Choose who pays for this discount.");
      return;
    }
    const value = Number(pct);
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      setValidationError("Discount must be between 0 and 100.");
      return;
    }
    if (startsAt && endsAt && startsAt > endsAt) {
      setValidationError("End date must be on or after the start date.");
      return;
    }

    await submission.run(async () => {
      setValidationError(null);
      setServerError(null);
      const input = {
        funding,
        percentage: value,
        status,
        startsAt: startsAt ? `${startsAt}T00:00:00.000Z` : null,
        endsAt: endsAt ? `${endsAt}T23:59:59.999Z` : null,
      };
      try {
        if (existing) {
          await updateRule.mutateAsync({ id: existing.id, ...input });
        } else {
          await createRule.mutateAsync({
            name: ruleName,
            ...input,
            eligibilityRows,
          });
        }
        toast.success(successMessage);
        setOpen(false);
      } catch (error) {
        const message = mutationFailure(error, "save this discount rule");
        setServerError(message);
        toast.error(message);
      }
    });
  }

  return (
    <>
      <Button
        type="button"
        aria-label={triggerAriaLabel}
        variant="ghost"
        size="sm-touch"
        className={cn(fullWidth && "w-full")}
        onClick={start}
      >
        {existing ? "Edit" : "Configure"}
      </Button>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!pending) setOpen(nextOpen);
        }}
        title={title}
        presentation="responsive-sheet"
      >
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">{description}</p>
          <div>
            <label
              className="mb-1 block text-sm font-medium"
              htmlFor={`${fieldId}-pct`}
            >
              Discount (0–100%)
            </label>
            <Input
              id={`${fieldId}-pct`}
              inputMode="decimal"
              value={pct}
              onChange={(event) => setPct(event.target.value)}
              disabled={pending}
            />
          </div>
          <div>
            <label
              className="mb-1 block text-sm font-medium"
              htmlFor={`${fieldId}-funding`}
            >
              Who pays for this discount?
            </label>
            <NativeSelect
              id={`${fieldId}-funding`}
              value={funding}
              onChange={(event) => {
                const value = event.target.value;
                if (value === "PLATFORM" || value === "CARRIER" || value === "")
                  setFunding(value);
              }}
              disabled={pending}
              aria-describedby={`${fieldId}-funding-help`}
            >
              <option value="">Choose who pays</option>
              <option value="CARRIER">Carrier pays</option>
              <option value="PLATFORM">EZX pays</option>
            </NativeSelect>
            <p
              id={`${fieldId}-funding-help`}
              className="mt-2 text-xs text-muted-foreground"
            >
              {funding === "CARRIER"
                ? "Reduces your carrier earnings on self-rated labels; EZX's fee stays unchanged. It cannot apply to labels purchased through ChitChats or another provider, and does not switch to EZX funding."
                : funding === "PLATFORM"
                  ? "Uses EZX's fee on each quote, including any service-specific override. Your carrier earnings stay unchanged. If that fee is zero, no discount applies; insufficient fees limit the discount."
                  : "Choose deliberately: carrier funding reduces your earnings; EZX funding is limited to EZX's fee. Saving an active rule does not guarantee the full requested discount."}
            </p>
          </div>
          <div>
            <label
              className="mb-1 block text-sm font-medium"
              htmlFor={`${fieldId}-status`}
            >
              Rule status
            </label>
            <NativeSelect
              id={`${fieldId}-status`}
              value={status}
              onChange={(event) =>
                setStatus(event.target.value as CarrierDiscountRuleStatus)
              }
              disabled={pending}
            >
              <option value="ACTIVE">Active</option>
              <option value="DRAFT">Draft</option>
              <option value="ARCHIVED">Archived</option>
            </NativeSelect>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label
                className="mb-1 block text-sm font-medium"
                htmlFor={`${fieldId}-starts`}
              >
                Starts (optional)
              </label>
              <Input
                id={`${fieldId}-starts`}
                type="date"
                value={startsAt}
                onChange={(event) => setStartsAt(event.target.value)}
                disabled={pending}
              />
            </div>
            <div>
              <label
                className="mb-1 block text-sm font-medium"
                htmlFor={`${fieldId}-ends`}
              >
                Ends (optional)
              </label>
              <Input
                id={`${fieldId}-ends`}
                type="date"
                value={endsAt}
                onChange={(event) => setEndsAt(event.target.value)}
                disabled={pending}
              />
            </div>
          </div>
          {(validationError || serverError) && (
            <p role="alert" className="text-sm text-destructive">
              {validationError ?? serverError}
            </p>
          )}
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <Button
              variant="ghost"
              size="touch"
              onClick={() => setOpen(false)}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button
              size="touch"
              onClick={() => void submit()}
              disabled={pending || pct.trim() === "" || funding === ""}
            >
              {pending ? "Saving..." : saveLabel}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
